/**
 * m3 echo Frontend - Early Access Disclaimer Dialog
 *
 * Modal dialog that displays the Early Access disclaimer.
 * Users must accept to continue or cancel to logout.
 *
 * Features:
 * - Shows headline and configurable text from backend
 * - "Abbrechen" button logs out the user
 * - "Akzeptieren" button accepts the disclaimer
 * - Cannot be dismissed by clicking outside or pressing Escape
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

interface EarlyAccessDisclaimerDialogProps {
  open: boolean;
  headline: string;
  text: string;
  onAccept: () => Promise<void>;
  onCancel: () => void;
}

export const EarlyAccessDisclaimerDialog: React.FC<EarlyAccessDisclaimerDialogProps> = ({
  open,
  headline,
  text,
  onAccept,
  onCancel,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAccept = async () => {
    setLoading(true);
    setError(null);
    try {
      await onAccept();
    } catch (err: any) {
      console.error('Error accepting disclaimer:', err);
      setError(err.message || 'Fehler beim Akzeptieren. Bitte versuchen Sie es erneut.');
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      onClose={(_, reason) => {
        // Prevent closing by clicking outside
        if (reason === 'backdropClick') return;
      }}
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 28 }} />
          <Typography variant="h6" component="span" fontWeight={600}>
            {headline}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Typography
          variant="body1"
          sx={{
            whiteSpace: 'pre-wrap',
            lineHeight: 1.7,
            color: 'text.secondary',
          }}
        >
          {text}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
        <Button
          onClick={handleCancel}
          color="inherit"
          disabled={loading}
          sx={{ mr: 1 }}
        >
          Abbrechen
        </Button>
        <Button
          onClick={handleAccept}
          variant="contained"
          disabled={loading}
          sx={{ minWidth: 120 }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} color="inherit" />
              <span>Wird verarbeitet...</span>
            </Box>
          ) : (
            'Akzeptieren'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EarlyAccessDisclaimerDialog;
